import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import SuccessMessage from '../success-message/success-message';
import { useAppSelector } from '../../services/store/hooks';
import { useUpdateProfileLifestylesMutation } from '../../services/store/api/userApi';
import styles from './profile-lifestyles.module.css';

interface Props {
  fromShow?: 'rega' | 'profile';
}

const ProfileLifestyles: React.FC<Props> = ({ fromShow = 'rega' }) => {
  const navigate = useNavigate();
  const { lifestyleCategories } = useAppSelector(state => state.dict);
  const { profile } = useAppSelector(state => state.user);
  const [formData, setFormData] = useState<{ lifestyles: number[] }>({ lifestyles: [] });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const btnSubmit = fromShow === 'rega' ? 'Продолжить' : 'Сохранить';

  const [
    updateProfileLifestyles,
    { isLoading, isError, error },
  ] = useUpdateProfileLifestylesMutation();

  const lifestylesByCategory = useMemo(() => {
    return lifestyleCategories.reduce<Record<number, number[]>>((acc, category) => {
      acc[category.id] = category.lifestyles.map(lifestyle => lifestyle.id);
      return acc;
    }, {});
  }, [lifestyleCategories]);

  const selectedByCategory = useMemo(() => {
    return lifestyleCategories.reduce<Record<number, number | undefined>>((acc, category) => {
      acc[category.id] = category.lifestyles.find(lifestyle =>
        formData.lifestyles.includes(lifestyle.id)
      )?.id;

      return acc;
    }, {});
  }, [formData.lifestyles, lifestyleCategories]);

  useEffect(() => {
    if (profile?.lifestyles) {
      setFormData({
        lifestyles: profile.lifestyles.map(lifestyle => lifestyle.id),
      });
    }
  }, [profile]);

  useEffect(() => {
    if (currentStep >= lifestyleCategories.length) {
      setCurrentStep(Math.max(0, lifestyleCategories.length - 1));
    }
  }, [currentStep, lifestyleCategories.length]);

  const handleLifestyleSelect = (categoryId: number, lifestyleId: number) => {
    const categoryLifestyleIds = lifestylesByCategory[categoryId] ?? [];
    setValidationError(null);

    setFormData(prev => {
      const withoutCurrentCategory = prev.lifestyles.filter(id => !categoryLifestyleIds.includes(id));

      return {
        lifestyles: [...withoutCurrentCategory, lifestyleId],
      };
    });
  };

  const currentCategory = lifestyleCategories[currentStep];
  const currentSelectedLifestyleId = currentCategory
    ? selectedByCategory[currentCategory.id]
    : undefined;
  const isLastStep = currentStep === lifestyleCategories.length - 1;
  const progress = lifestyleCategories.length
    ? ((currentStep + 1) / lifestyleCategories.length) * 100
    : 0;

  const moveToNextStep = () => {
    if (!currentSelectedLifestyleId) {
      setValidationError('Выберите один вариант, чтобы продолжить');
      return;
    }

    setValidationError(null);
    setCurrentStep(step => Math.min(step + 1, lifestyleCategories.length - 1));
  };

  const moveToPreviousStep = () => {
    setValidationError(null);
    setCurrentStep(step => Math.max(step - 1, 0));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!currentSelectedLifestyleId) {
      setValidationError('Выберите один вариант, чтобы продолжить');
      return;
    }

    if (!isLastStep) {
      moveToNextStep();
      return;
    }

    const missingCategoryIndex = lifestyleCategories.findIndex(category => !selectedByCategory[category.id]);

    if (missingCategoryIndex !== -1) {
      setCurrentStep(missingCategoryIndex);
      setValidationError('Выберите вариант в каждой категории');
      return;
    }

    setValidationError(null);

    try {
      await updateProfileLifestyles(formData).unwrap();

      if (fromShow === 'rega') {
        navigate('/profile-photos');
      } else {
        setShowSuccessMessage(true);

        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      }
    } catch {
      // Ошибка отобразится через isError.
    }
  };

  if (!lifestyleCategories?.length) {
    return null;
  }

  const errorMessage =
    (error as FetchBaseQueryError & {
      data?: { message?: string };
    })?.data?.message ?? 'Ошибка при сохранении образа жизни';

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.hint}>
          Выберите один вариант в каждой категории.
        </div>

        <div className={styles.progressHead}>
          <span>
            {currentStep + 1} / {lifestyleCategories.length}
          </span>

          <div className={styles.progressTrack}>
            <div
              className={styles.progressBar}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {currentCategory && (
          <div key={currentCategory.id} className={styles.categoryStep}>
            <div className={styles.categoryTitle}>
              {currentCategory.title}
            </div>

            <div className="interestsContainer">
              {currentCategory.lifestyles.map(lifestyle => (
                <button
                  key={lifestyle.id}
                  type="button"
                  className={`interestBtn ${
                    formData.lifestyles.includes(lifestyle.id)
                      ? 'interestBtnActive'
                      : ''
                  }`}
                  onClick={() => handleLifestyleSelect(currentCategory.id, lifestyle.id)}
                >
                  {lifestyle.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {validationError && (
          <div className="error-box">
            {validationError}
          </div>
        )}

        <div className={styles.actions}>
          {currentStep > 0 && (
            <button
              type="button"
              className="btn-link btn-link-cancel"
              onClick={moveToPreviousStep}
              disabled={isLoading}
            >
              Назад
            </button>
          )}

          <button
            type="submit"
            className="btn-link"
            disabled={isLoading}
          >
            {isLoading ? 'Сохранение...' : isLastStep ? btnSubmit : 'Далее'}
          </button>
        </div>
      </form>

      {showSuccessMessage && (
        <SuccessMessage>
          Образ жизни успешно сохранен!
        </SuccessMessage>
      )}

      {isError && (
        <div className="error-box">
          {errorMessage}
        </div>
      )}
    </>
  );
};

export default ProfileLifestyles;
